import type { GranularityType } from "@/hooks/useEvolutionChartData";

/**
 * Formata o rótulo de período do eixo X / tooltip em pt-BR — o valor bruto
 * (usado internamente pra chave/ordenação) vem do backend em ISO
 * ("2026-01-05" pra dia/semana, "2026-01" pra mês). Figma (node 124:13041)
 * mostra sempre dd/MM/aa, mesmo pra granularidade semanal (data de início da
 * semana); mês usa MM/aa pelo mesmo padrão numérico.
 */
export function formatPeriodLabel(period: string, granularity: GranularityType): string {
  if (granularity === "month") {
    const [year, month] = period.split("-");
    if (!year || !month) return period;
    return `${month}/${year.slice(2)}`;
  }

  const [year, month, day] = period.split("-");
  if (!year || !month || !day) return period;
  return `${day}/${month}/${year.slice(2)}`;
}

/**
 * Rótulo do eixo Y: abrevia por extenso ("2,5 mil", "1,5 mi"), nunca "K"/"M".
 * "4,3K kg" gerava dúvida de leitura no cliente, e "M" em pt-BR pode ser lido
 * como "mil" (mesmo motivo de `fmtCompactNumber`). A unidade não vai no tick —
 * aparece uma vez sobre o eixo —, senão "100.000 kg" quebra em duas linhas no
 * eixo.
 *
 * Abreviar aqui não esconde valor: os ticks são números redondos escolhidos
 * pelo Recharts. O valor exato fica no tooltip.
 *
 * Espaço não separável entre número e sufixo: o <Text> do Recharts quebra o
 * tick em espaço comum quando estima que não cabe no eixo — "300 mil" saía em
 * duas linhas (visto com o Roldão em stg).
 */
const NBSP = "\u00A0";

export function formatEvolutionAxisTick(value: number): string {
  if (!Number.isFinite(value) || value <= 0) return "0";

  const truncateToOneDecimal = (v: number) => Math.trunc(v * 10) / 10;
  const fmt = (v: number) => v.toLocaleString("pt-BR", { maximumFractionDigits: 1 });

  if (value >= 1_000_000) return `${fmt(truncateToOneDecimal(value / 1_000_000))}${NBSP}mi`;
  if (value >= 1_000) return `${fmt(truncateToOneDecimal(value / 1_000))}${NBSP}mil`;
  return Math.trunc(value).toLocaleString("pt-BR");
}
